import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { SortOptionsEditor } from "@/components/SortOptionsEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Save, PenSquare, Mail, Bell, Ban, X, Plus, RotateCcw } from "lucide-react";

/* Catene / operatori sempre esclusi dalla qualificazione Rivenditori (pipeline unica). */
const ExcludedChainsCard = () => {
  const [chains, setChains] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/settings/excluded-chains")
      .then(({ data }) => { setChains(data.chains || []); setDefaults(data.default || []); })
      .catch(() => {});
  }, []);
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (chains.some((c) => c.toLowerCase() === v.toLowerCase())) { toast.info("Già in elenco"); return; }
    setChains((p) => [...p, v]); setDraft("");
  };
  const save = async (list) => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/excluded-chains", { chains: list ?? chains });
      setChains(data.chains || []);
      toast.success("Elenco esclusioni salvato: verrà applicato a tutte le ricerche Rivenditori");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Salvataggio fallito"); }
    finally { setSaving(false); }
  };
  return (
    <div className="rounded-2xl border border-border bg-brema-card p-5" data-testid="excluded-chains-card">
      <div className="mb-2 flex items-center gap-2 text-foreground">
        <Ban className="h-5 w-5 text-brand" />
        <h3 className="font-semibold">Catene / operatori sempre esclusi</h3>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">
        Grandi catene ed e-commerce ad alto volume scartati automaticamente (con motivo negli Esclusi) da Autopilot, Avvia ricerca AI, AI Discovery Search e Comando Rapido. Il confronto è su nome attività e dominio del sito.
      </p>
      <div className="mb-3 flex flex-wrap gap-1.5" data-testid="excluded-chains-list">
        {chains.length === 0 && <span className="text-xs text-muted-foreground">Nessuna catena esclusa.</span>}
        {chains.map((c, i) => (
          <span key={c} className="inline-flex items-center gap-1 rounded-full border border-border bg-brema-bg px-2.5 py-1 text-xs text-foreground" data-testid={`excluded-chain-${i}`}>
            {c}
            <button type="button" aria-label={`Rimuovi ${c}`} data-testid={`excluded-chain-remove-${i}`}
              onClick={() => setChains((p) => p.filter((x) => x !== c))}
              className="rounded-full p-0.5 text-muted-foreground hover:text-danger"><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Input value={draft} placeholder="Es. Decathlon" onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} data-testid="excluded-chains-input" className="h-9 sm:max-w-xs" />
        <Button type="button" variant="secondary" onClick={add} data-testid="excluded-chains-add"
          className="h-9 rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
          <Plus className="mr-1 h-3.5 w-3.5" /> Aggiungi
        </Button>
        <Button type="button" variant="secondary" onClick={() => save(defaults)} disabled={saving} data-testid="excluded-chains-reset"
          className="h-9 rounded-full border border-border bg-transparent text-xs text-muted-foreground hover:bg-brema-hover">
          <RotateCcw className="mr-1 h-3.5 w-3.5" /> Ripristina predefinite
        </Button>
        <Button onClick={() => save()} disabled={saving} data-testid="excluded-chains-save"
          className="h-9 rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90 sm:ml-auto">
          {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Salvataggio…</> : <><Save className="mr-2 h-4 w-4" /> Salva</>}
        </Button>
      </div>
    </div>
  );
};

const FIELDS = [
  { key: "signer_name", label: "Nome firmatario", placeholder: "Giovanna Blasi" },
  { key: "role_line", label: "Azienda / Ruolo", placeholder: "Brema Bike - Sales & Mkt Relations" },
  { key: "company", label: "Ragione sociale", placeholder: "ABOVE 59 SRL" },
  { key: "address1", label: "Indirizzo", placeholder: "Fraz. Cancelli, 128" },
  { key: "address2", label: "Città / Paese", placeholder: "60044 FABRIANO (AN) - Italy" },
  { key: "phone", label: "Telefono", placeholder: "Tel. +39 0732 3880" },
  { key: "website", label: "Sito web (testo)", placeholder: "www.brema-1969.com" },
  { key: "website_url", label: "Sito web (link)", placeholder: "https://www.brema-1969.com" },
];

const esc = (s) => (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const buildSigHtml = (s) => {
  if (!s) return "";
  const parts = [];
  if (s.signer_name) parts.push("<strong>" + esc(s.signer_name) + "</strong>");
  ["role_line", "company", "address1", "address2", "phone"].forEach((k) => { if (s[k]) parts.push(esc(s[k])); });
  let body = parts.join("<br>");
  if (s.website) {
    const url = (s.website_url || "").trim() || ("https://" + s.website.replace(/^\/+/, ""));
    const web = '<a href="' + esc(url) + '" style="color:#c8102e;text-decoration:none">' + esc(s.website) + "</a>";
    body = body ? body + "<br>" + web : web;
  }
  return '<div style="margin-top:6px;font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#1a1a1a;line-height:1.5">' + body + "</div>";
};

export default function Settings() {
  const [sig, setSig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyDefault, setNotifyDefault] = useState("");
  const [marketingEmail, setMarketingEmail] = useState("");
  const [marketingDefault, setMarketingDefault] = useState("");
  const [savingNotify, setSavingNotify] = useState(false);

  useEffect(() => {
    api.get("/settings/email-signature")
      .then(({ data }) => setSig(data.signature))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail) || "Caricamento fallito"))
      .finally(() => setLoading(false));
    api.get("/settings/notify-email")
      .then(({ data }) => {
        setNotifyEmail(data.email || ""); setNotifyDefault(data.default || "");
        setMarketingEmail(data.marketing_email || ""); setMarketingDefault(data.marketing_default || "");
      })
      .catch(() => {});
  }, []);

  const setField = (k, v) => setSig((s) => ({ ...s, [k]: v }));

  const saveNotify = async () => {
    setSavingNotify(true);
    try {
      const { data } = await api.put("/settings/notify-email", { email: notifyEmail.trim(), marketing_email: marketingEmail.trim() });
      setNotifyEmail(data.email || ""); setMarketingEmail(data.marketing_email || "");
      toast.success("Destinatari notifiche aggiornati");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Salvataggio fallito");
    } finally { setSavingNotify(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/email-signature", sig);
      setSig(data.signature);
      toast.success("Firma aggiornata: sarà usata in tutte le prossime email");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Salvataggio fallito");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader
        eyebrow="Impostazioni"
        title="Impostazioni piattaforma"
        subtitle="Firma email ufficiale, destinatario notifiche, catene sempre escluse dalla ricerca Rivenditori e voci del menu Ordina per dei Finder. Firma e nota GDPR vengono agganciate automaticamente a tutte le email (Autopilot, campagne, ambassador)."
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-border bg-brema-card p-5" data-testid="notify-email-card">
          <div className="mb-2 flex items-center gap-2 text-foreground">
            <Bell className="h-5 w-5 text-brand" />
            <h3 className="font-semibold">Destinatari notifiche</h3>
          </div>
          <p className="mb-4 text-sm text-muted-foreground">
            Due indirizzi distinti: <b>Commerciale</b> riceve ordini, richieste e messaggi dei rivenditori dal Portale B2B; <b>Marketing</b> riceve campionature, richieste materiale, candidature, kit e messaggi inviati da Press Lounge e Ambassador Lounge.
          </p>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Commerciale · Ordini & Rivenditori (Portale B2B)</Label>
              <Input type="email" value={notifyEmail} placeholder={notifyDefault || "sales2@brema-1969.com"}
                onChange={(e) => setNotifyEmail(e.target.value)} data-testid="notify-email-input" className="h-9" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Marketing · Press Lounge & Ambassador Lounge</Label>
              <Input type="email" value={marketingEmail} placeholder={marketingDefault || "marketing@brema-1969.com"}
                onChange={(e) => setMarketingEmail(e.target.value)} data-testid="notify-marketing-email-input" className="h-9" />
            </div>
            <Button onClick={saveNotify} disabled={savingNotify} data-testid="notify-email-save"
              className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
              {savingNotify ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Salvataggio…</> : <><Save className="mr-2 h-4 w-4" /> Salva</>}
            </Button>
          </div>
        </div>
        <ExcludedChainsCard />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-border bg-brema-card p-5" data-testid="signature-editor">
          <div className="mb-4 flex items-center gap-2 text-foreground">
            <PenSquare className="h-5 w-5 text-brand" />
            <h3 className="font-semibold">Dati firma</h3>
          </div>
          {loading || !sig ? (
            <div className="flex items-center gap-2 py-10 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /> Caricamento…</div>
          ) : (
            <div className="space-y-3">
              {FIELDS.map((f) => (
                <div key={f.key} className="space-y-1.5">
                  <Label className="text-xs">{f.label}</Label>
                  <Input value={sig[f.key] ?? ""} placeholder={f.placeholder}
                    onChange={(e) => setField(f.key, e.target.value)} data-testid={`signature-${f.key}`} className="h-9" />
                </div>
              ))}
              <Button onClick={save} disabled={saving} data-testid="signature-save"
                className="w-full rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
                {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Salvataggio…</> : <><Save className="mr-2 h-4 w-4" /> Salva firma</>}
              </Button>
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-border bg-brema-card p-5" data-testid="signature-preview-card">
          <div className="mb-4 flex items-center gap-2 text-foreground">
            <Mail className="h-5 w-5 text-brand" />
            <h3 className="font-semibold">Anteprima blocco firma</h3>
          </div>
          <div className="rounded-lg border border-border bg-white p-5">
            <div className="mb-3 text-sm text-[#1a1a1a]" style={{ fontFamily: "Arial, Helvetica, sans-serif" }}>
              <div style={{ marginBottom: 8 }}>… (corpo email)</div>
              <div style={{ marginTop: 16, fontSize: 14 }}>Un cordiale saluto / Best regards,</div>
            </div>
            <div data-testid="signature-preview" dangerouslySetInnerHTML={{ __html: buildSigHtml(sig) }} />
            <div className="mt-4 border-t border-[#e2e2e6] pt-3 text-[11px] text-[#8a8a8a]" style={{ fontFamily: "Arial, Helvetica, sans-serif" }}>
              INFORMATIVA GDPR 679/16 — Unsubscribe … <span className="italic">(gestita dal sistema)</span>
            </div>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">Il saluto di chiusura si adatta automaticamente alla lingua del destinatario (IT/EN/FR/ES/DE).</p>
        </div>
      </div>

      <div className="mt-6">
        <SortOptionsEditor />
      </div>
    </div>
  );
}
